import { TERMS_VERSION, seatAddressOf, siteSameAsSeat } from "@szerviz/core";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { LegalDocument } from "../screens/Legal";
import type { LegalDoc } from "../legal";
import { useApp } from "../context";
import type { ContractDetails } from "../data";
import { ErrorText } from "./ErrorText";

/**
 * Before the owner works in the program, the workshop accepts the general terms
 * (with the highlighted liability and amendment clauses) and the data processing
 * agreement. Shown again when TERMS_VERSION changes.
 */
export function TermsGate({ onAccept, onSignOut }: { onAccept: (details: ContractDetails) => Promise<void>; onSignOut: () => void }) {
  const { t, i18n } = useTranslation();
  const { workshop, store } = useApp();
  const general = workshop.general as Record<string, string | null | undefined>;
  // who concludes the contract: the business, recorded with the acceptance
  const [company, setCompany] = useState(workshop.name);
  const [address, setAddress] = useState(seatAddressOf(general) ?? "");
  // where the car is (owner, 2026-11-04): asked here at the first setup, the seat unless the tick is taken off
  const [sameAsSeat, setSameAsSeat] = useState(siteSameAsSeat(general));
  const [site, setSite] = useState(siteSameAsSeat(general) ? "" : (general.workshopAddress ?? ""));
  const [taxNumber, setTaxNumber] = useState(general.workshopTaxNumber ?? "");
  const [regNumber, setRegNumber] = useState(general.workshopRegNumber ?? "");
  const [terms, setTerms] = useState(false);
  const [dpa, setDpa] = useState(false);
  const [reading, setReading] = useState<LegalDoc | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const missing = [
    !company.trim() && t("terms.gate.company"),
    !address.trim() && t("terms.gate.address"),
    !sameAsSeat && !site.trim() && t("setting.workshopAddress.label"),
    !taxNumber.trim() && t("terms.gate.taxNumber"),
    !terms && t("terms.gate.termsShort"),
    !dpa && t("terms.gate.dpaShort"),
  ].filter(Boolean) as string[];

  const accept = async () => {
    setBusy(true);
    setError(null);
    try {
      await store.saveGeneralSettings(workshop.id, {
        ...(workshop.general as Record<string, number | string | boolean | null>),
        seatAddress: address.trim(), workshopAddressSameAsSeat: sameAsSeat, workshopAddress: sameAsSeat ? address.trim() : site.trim(),
      });
      await onAccept({ companyName: company.trim(), address: address.trim(), taxNumber: taxNumber.trim(), regNumber: regNumber.trim() || null });
    } catch (err) {
      setError(err);
      setBusy(false);
    }
  };

  return (
    <div className="modal-backdrop">
      <div className="modal terms-gate" role="dialog" aria-modal="true" aria-labelledby="terms-gate-title">
        <h2 id="terms-gate-title">{t("terms.gate.title")}</h2>
        <p>{t("terms.gate.intro", { version: TERMS_VERSION })}</p>
        <fieldset className="contract-party">
          <legend>{t("terms.gate.party")}</legend>
          <label className="field">
            <span>{t("terms.gate.company")}</span>
            <input id="contract-company" value={company} onChange={(e) => setCompany(e.target.value)} />
          </label>
          <label className="field">
            <span>{t("terms.gate.address")}</span>
            <input id="contract-address" value={address} onChange={(e) => setAddress(e.target.value)} />
          </label>
          <label className="check-row">
            <input type="checkbox" checked={sameAsSeat} onChange={(e) => setSameAsSeat(e.target.checked)} />
            <span>{t("setting.workshopAddressSameAsSeat.label")}</span>
          </label>
          {!sameAsSeat && (
            <label className={`field${site.trim() ? "" : " invalid"}`}>
              <span>{t("setting.workshopAddress.label")}</span>
              <input id="contract-site" value={site} onChange={(e) => setSite(e.target.value)} />
            </label>
          )}
          <label className="field">
            <span>{t("terms.gate.taxNumber")}</span>
            <input id="contract-tax" value={taxNumber} onChange={(e) => setTaxNumber(e.target.value)} />
          </label>
          <label className="field">
            <span>{t("terms.gate.regNumber")}</span>
            <input id="contract-reg" value={regNumber} onChange={(e) => setRegNumber(e.target.value)} />
          </label>
          <p className="muted">{t("terms.gate.partyNote")}</p>
        </fieldset>
        <p className="warn-box">{t("terms.gate.highlight")}</p>
        <label className={`check-row terms-check${terms ? "" : " unchecked"}`}>
          <input type="checkbox" checked={terms} onChange={(e) => setTerms(e.target.checked)} />
          <span>{t("terms.gate.acceptTerms")}</span>
        </label>
        <button type="button" className="link" onClick={() => setReading(reading === "terms" ? null : "terms")}>
          {t(reading === "terms" ? "terms.gate.hide" : "terms.gate.read", { doc: t("legal.doc.terms") })}
        </button>
        <label className={`check-row terms-check${dpa ? "" : " unchecked"}`}>
          <input type="checkbox" checked={dpa} onChange={(e) => setDpa(e.target.checked)} />
          <span>{t("terms.gate.acceptDpa")}</span>
        </label>
        <button type="button" className="link" onClick={() => setReading(reading === "dpa" ? null : "dpa")}>
          {t(reading === "dpa" ? "terms.gate.hide" : "terms.gate.read", { doc: t("legal.doc.dpa") })}
        </button>
        {reading && (
          <div className="terms-reader">
            <LegalDocument doc={reading} language={i18n.language} />
          </div>
        )}
        <ErrorText error={error} />
        <div className="actions">
          {missing.length > 0 && <span className="missing">{t("required.stillMissing", { fields: missing.join(", ") })}</span>}
          <button type="button" onClick={onSignOut}>
            {t("auth.signOut")}
          </button>
          <button type="button" className="primary" disabled={missing.length > 0 || busy} onClick={() => void accept()}>
            {t("terms.gate.accept")}
          </button>
        </div>
      </div>
    </div>
  );
}
